/**
 * The app pages' addresses, on the server that serves them:
 *
 * - `/`: your teams (the last one you looked at opens), or signing in;
 * - `/t/<team>`: one team: its spaces, members and invites;
 * - `/join/<code>`: an invite to accept (signing in or making an account first);
 * - `/account`: your account's safety: password, two-step sign-in, devices, activity, your data.
 *
 * Any other address is the editor's (`/s/<space>`, `/board`). After signing in, `?next=` takes
 * the page back where it was turned away (a space only, so a link cannot send people elsewhere).
 */
import { inviteOf } from "../client/space";

/** Which app page an address shows. */
export type PageRoute = { page: "home" } | { page: "team"; team: string } | { page: "join"; code: string } | { page: "account" };

/** Where this browser remembers the team last looked at (opened again at `/`). */
export const LAST_TEAM_KEY = "developer-app-last-team-v1";

/** The app page at `pathname`, or `null` when it is the editor's. */
export function pageRoute(pathname: string): PageRoute | null {
  if (pathname === "/" || pathname === "") return { page: "home" };
  if (pathname === "/account" || pathname === "/account/") return { page: "account" };
  const team = /^\/t\/([^/]+)\/?$/.exec(pathname);
  if (team) return { page: "team", team: decodeURIComponent(team[1]!) };
  const code = inviteOf(pathname);
  if (code) return { page: "join", code };
  return null;
}

/** The address of a route. */
export function routePath(route: PageRoute): string {
  if (route.page === "team") return `/t/${encodeURIComponent(route.team)}`;
  if (route.page === "join") return `/join/${encodeURIComponent(route.code)}`;
  if (route.page === "account") return "/account";
  return "/";
}

/** Where to go after signing in: `?next=` when it is a space of this server, else nowhere. */
export function nextPath(search: string): string | null {
  const next = new URLSearchParams(search).get("next");
  return next && /^\/s\/[A-Za-z0-9_-]+$/.test(next) ? next : null;
}

/** The ticket (`?auth=`) or the failure (`?auth_error=`) a sign-in with Google or GitHub came back with. */
export function signInResult(search: string): { ticket: string } | { error: string } | null {
  const params = new URLSearchParams(search);
  const ticket = params.get("auth");
  if (ticket) return { ticket };
  const error = params.get("auth_error");
  return error ? { error } : null;
}

/** `search` without the parameters a sign-in came back with (`auth`, `auth_error`). */
export function withoutReturns(search: string): string {
  const params = new URLSearchParams(search);
  for (const name of ["auth", "auth_error"]) params.delete(name);
  const query = params.toString();
  return query ? `?${query}` : "";
}
